/**
 * A ranger's service file (04 §5): every mission he has flown, newest first, with what it cost him
 * and what it won him.
 *
 * `ServiceRecord` on the dossier card counts how MANY - fourteen missions, ninety-six kills. This
 * says WHICH, and it is the only place in the game that remembers that the Bronze Star came off the
 * prison camp and not the bridge. It is written by `recordFlight` at the one point a mission is
 * reported, so it starts empty for a ranger who was already serving when the file was introduced.
 */

import './serviceFile.css';
import type { MedalId, ServiceEntry } from '../../../meta/types';
import { RANKS } from '../../../meta/types';
import { box, el } from '../dom';
import type { Describe } from '../text';
import { safeDescribe } from '../text';
import { stamp } from './stamp';

/** How a mission ended, in the debrief's words, with the tone the row is stamped in. */
const FATE: Record<ServiceEntry['outcome'], { text: string; tone: 'green' | 'amber' | 'red' }> = {
  extracted: { text: 'EXTRACTED', tone: 'green' },
  walkedOut: { text: 'WALKED OUT', tone: 'amber' },
  captured: { text: 'CAPTURED', tone: 'red' },
  kia: { text: 'KILLED', tone: 'red' },
  scrubbed: { text: 'SCRUBBED', tone: 'amber' },
};

/**
 * What the row says happened, in one phrase. A mission is not simply won or lost: a man can do what
 * he was sent to do and still not come home, and the file has to be able to say so. Pure.
 */
export function outcomeText(entry: ServiceEntry): string {
  const fate = FATE[entry.outcome];
  if (entry.outcome === 'extracted') return entry.objectiveComplete ? 'OBJECTIVE MET' : 'CAME HOME, OBJECTIVE MISSED';
  if (entry.objectiveComplete) return `OBJECTIVE MET - ${fate.text}`;
  return fate.text;
}

/** Newest first: the file is written oldest-first, because that is the cheap end to append to. Pure. */
export const fileOrder = (entries: readonly ServiceEntry[]): ServiceEntry[] => [...entries].reverse();

function medalRow(medals: readonly MedalId[], describe: Describe): HTMLElement | null {
  if (medals.length === 0) return null;
  const row = el('div', 'menu-file-awards');
  for (const id of medals) {
    const d = safeDescribe(describe, id);
    const tag = el('span', 'menu-tag menu-file-award', d.name);
    tag.title = d.text;
    row.append(tag);
  }
  return row;
}

function cell(key: string, value: string): HTMLElement {
  return box('menu-file-cell', el('span', 'menu-file-key', key), el('span', 'menu-file-value', value));
}

function row(entry: ServiceEntry, describe: Describe): HTMLElement {
  const fate = FATE[entry.outcome];
  const root = el('div', 'menu-file-row');
  root.append(
    box(
      'menu-file-head',
      stamp({ text: fate.text, tone: fate.tone }, true),
      el('span', 'menu-file-mission', entry.mission),
    ),
    box(
      'menu-file-line',
      el('span', 'menu-file-operation', `OPERATION ${entry.operation}`),
      el('span', 'menu-file-result', outcomeText(entry)),
    ),
    box(
      'menu-file-cells',
      cell('THREAT', String(entry.threat)),
      cell('SCORE', entry.score > 0 ? entry.score.toLocaleString('en-GB') : '-'),
      cell('KILLS', String(entry.kills)),
      cell('WOUNDS', String(entry.wounds)),
    ),
  );
  if (entry.promotedTo !== undefined) {
    const to = RANKS[entry.promotedTo] ?? RANKS[0];
    root.append(box('menu-file-note', stamp({ text: 'PROMOTED', tone: 'green' }, true), el('span', '', `TO ${to}`)));
  }
  const medals = medalRow(entry.medals, describe);
  if (medals) root.append(medals);
  return root;
}

/**
 * The whole file. `empty` says why there is nothing to show, which for a ranger who was already
 * serving before this existed is not the same as "he has never flown".
 */
export function serviceFile(entries: readonly ServiceEntry[], describe: Describe, missions: number): HTMLElement {
  const root = el('div', 'menu-file');
  const shown = fileOrder(entries);
  if (shown.length === 0) {
    // Half the roster are women, so the file speaks of the ranger and not of "him".
    const why = missions > 0
      ? `NO MISSION-BY-MISSION RECORD. THOSE ${missions} MISSION${missions === 1 ? ' WAS' : 'S WERE'} FLOWN`
        + ' BEFORE THE FILES WERE KEPT; FROM THE NEXT ONE THEY WILL BE.'
      : 'NO MISSIONS FLOWN YET.';
    root.append(el('div', 'menu-empty menu-prose', why));
    return root;
  }
  const kept = shown.length < missions
    ? ` - THE LAST ${shown.length} OF ${missions}`
    : '';
  root.append(el('div', 'menu-file-title', `SERVICE FILE${kept}`));
  for (const entry of shown) root.append(row(entry, describe));
  return root;
}
